import { HttpInterceptorFn, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError, of, NEVER, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { AuthService } from './auth.service';
import { AuthPathsService, AUTH_STORAGE_KEYS } from './multi-tenant-auth';
import { STORAGE_TOKEN } from './storage.token';
import { DuplicateEmailError, InactiveUserError, PortalAccessDeniedError, SessionExpiredError } from './errors/errors';
import { PORTAL_ACCESS_VALIDATION_ERRORS } from './portal-access-validation-errors';
import { TermsAcceptanceService } from './terms-acceptance.service';

const emptyOkResponse = () => of(new HttpResponse({ status: 200, body: null }));

function handle403(
  error: HttpErrorResponse,
  termsService: TermsAcceptanceService,
  authService: AuthService,
  router: Router
): Observable<never> | null {
  const errorType = (error.error as Record<string, unknown>)?.['type'];

  if (errorType === 'terms-acceptance-required') {
    // Mark terms as required directly in the cache (no API call) so that
    // guard checks during the upcoming /terms navigation resolve immediately
    // from the cache.
    termsService.markRequired();
    const portalType = authService.getCurrentPortalType();
    const termsPath: string[] = portalType === 'student' ? ['/student', 'terms'] : ['/terms'];

    router.navigate(termsPath);
    return NEVER;
  }

  return null;
}

function handle401(
  error: HttpErrorResponse,
  authService: AuthService,
  authPaths: AuthPathsService,
  sessionStorage: Storage
): Observable<HttpResponse<unknown>> {
  const errorBody = error.error as Record<string, unknown> | null;
  const errorType = errorBody && typeof errorBody === 'object' ? (errorBody['type'] as string) : null;

  switch (errorType) {
    case 'portal-access-denied':
      return throwError(() => new PortalAccessDeniedError());

    case PORTAL_ACCESS_VALIDATION_ERRORS.DUPLICATE_EMAIL:
      return throwError(() => new DuplicateEmailError());

    case PORTAL_ACCESS_VALIDATION_ERRORS.INACTIVE_USER:
      return throwError(() => new InactiveUserError());

    case PORTAL_ACCESS_VALIDATION_ERRORS.NOT_IN_PORTAL_GROUP:
    case PORTAL_ACCESS_VALIDATION_ERRORS.INVALID_EMAIL_DOMAIN:
    case PORTAL_ACCESS_VALIDATION_ERRORS.PROVISIONING_FAILED: {
      let errorData: { type: string; emailDomain?: string };
      if (errorType === PORTAL_ACCESS_VALIDATION_ERRORS.INVALID_EMAIL_DOMAIN) {
        errorData = {
          type: PORTAL_ACCESS_VALIDATION_ERRORS.INVALID_EMAIL_DOMAIN,
          emailDomain: errorBody ? ((errorBody['emailDomain'] as string) ?? '') : '',
        };
      } else if (errorType === PORTAL_ACCESS_VALIDATION_ERRORS.NOT_IN_PORTAL_GROUP) {
        errorData = { type: PORTAL_ACCESS_VALIDATION_ERRORS.NOT_IN_PORTAL_GROUP };
      } else {
        errorData = { type: PORTAL_ACCESS_VALIDATION_ERRORS.PROVISIONING_FAILED };
      }
      sessionStorage.setItem(AUTH_STORAGE_KEYS.pendingValidationError, JSON.stringify(errorData));
      // Invalidate session asynchronously to prevent blocking the current error flow
      setTimeout(() => authService.logout({ showNotification: false }), 0);
      // Return successful empty response to prevent "no element in sequence" error
      return emptyOkResponse();
    }

    default: {
      // Default 401 handling (session expired) — clear any stale tokens
      authService.clearTokens();
      sessionStorage.setItem(AUTH_STORAGE_KEYS.showSessionExpiredNotification, 'true');

      const loginPath = authPaths.getLoginPath(authService.getCurrentPortalType());
      return throwError(() => new SessionExpiredError().setContext({ loginPath }));
    }
  }
}

export const portalAccessDeniedInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const authPaths = inject(AuthPathsService);
  const sessionStorage = inject(STORAGE_TOKEN);
  const router = inject(Router);
  const termsService = inject(TermsAcceptanceService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      if (error.status === 403) {
        return handle403(error, termsService, authService, router) ?? throwError(() => error);
      }
      if (error.status === 401) {
        return handle401(error, authService, authPaths, sessionStorage);
      }

      return throwError(() => error);
    })
  );
};
